import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { CardMedia, useTheme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import Parser from "rss-parser";
import { Link } from "react-router-dom";
import { LocalizationLanguage } from "@green0meter/api-client";

import { Log } from "@helpers";
import {
  Card,
  CardContent,
  Pagination,
  Skeleton,
  Box,
  Button,
  Grid,
  Typography,
  Chip,
  CardActions,
} from "@components/base";

interface Article {
  title: string;
  link: string;
  pubDate: string;
  description: string;
  imageUrl: string;
}

const fetchArticles = async (
  locale: LocalizationLanguage,
  showAll: boolean,
): Promise<Article[]> => {
  try {
    const parser = new Parser({
      customFields: {
        item: ["description", "enclosure"],
      },
    });

    const response = await fetch(
      `https://api.allorigins.win/get?url=${encodeURIComponent(
        "https://www.greenometer.com/blog-feed.xml",
      )}`,
    );

    const data = await response.json();
    const feed = await parser.parseString(data.contents);

    if (!feed.items) {
      throw new Error("No items found in the RSS feed.");
    }

    const filteredArticles = feed.items.filter((item) => {
      if (locale === LocalizationLanguage.SK || locale === LocalizationLanguage.CZ) {
        return item.link?.includes("/cs/");
      } else {
        return !item.link?.includes("/cs/");
      }
    });

    const articles = filteredArticles.map((item) => ({
      title: item.title || "No title",
      link: item.link || "#",
      pubDate: item.pubDate || "",
      description: item.description || "No description available.",
      imageUrl: item.enclosure?.url || "https://via.placeholder.com/300",
    }));

    return showAll ? articles : articles.slice(0, 3);
  } catch (error) {
    Log("Error fetching articles:", error);
    throw new Error("Failed to load RSS feed. Please try again later.");
  }
};

const decodeHtml = (html: string): string => {
  const textarea = document.createElement("textarea");

  textarea.innerHTML = html;

  return textarea.value;
};

const EsgAcademyFeed: FC<{ locale: LocalizationLanguage }> = ({ locale }) => {
  const { t } = useTranslation();

  const [showAll, setShowAll] = useState(false);
  const { palette } = useTheme();
  const [page, setPage] = useState(1);
  const itemsPerPage = 9;
  const {
    data: articles,
    error,
    isLoading,
  } = useQuery({
    queryKey: ["RSS_FEED", locale, showAll],
    queryFn: () => fetchArticles(locale, showAll),
  });

  const totalPages = articles ? Math.ceil(articles.length / itemsPerPage) : 1;
  const paginatedArticles = showAll
    ? articles?.slice((page - 1) * itemsPerPage, page * itemsPerPage)
    : articles?.slice(0, 3);

  return (
    <Card sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle1">{t("esgAcademyFeed.title")}</Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            setShowAll(!showAll);
            setPage(1);
          }}
        >
          {showAll ? t("esgAcademyFeed.showLess") : t("esgAcademyFeed.viewAll")}
        </Button>
      </Box>
      {isLoading && (
        <Grid container spacing={2}>
          {Array.from(new Array(3)).map((_, index) => (
            <Grid size={4} key={index}>
              <Skeleton variant="rectangular" height={400} />
              <Skeleton />
              <Skeleton width="60%" />
            </Grid>
          ))}
        </Grid>
      )}
      {error && <Typography color="error">{error.message}</Typography>}
      {!isLoading && !error && (
        <>
          <Grid container spacing={2}>
            {paginatedArticles?.map((article, index) => (
              <Grid size={4} key={`${article.title}_${index}`}>
                <Box
                  key={index}
                  sx={{
                    borderRadius: 2,
                    boxShadow: 1,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                  style={{
                    background: "#E8F1F8",
                    border: `4px solid white`,
                    boxShadow: `0 0 0 2px ${palette.primary.main}`,
                  }}
                >
                  <CardMedia
                    component="img"
                    height="400"
                    image={article.imageUrl}
                    alt={article.title}
                    sx={{
                      borderRadius: 2,
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                    }}
                  />
                  <CardContent
                    sx={{
                      flex: 1,
                      padding: 0,
                      paddingX: 2,
                    }}
                  >
                    <Box>
                      <Chip
                        label="ESG Unpacked"
                        color="primary"
                        size="small"
                        variant="filled"
                        sx={{ mb: 1 }}
                      />
                    </Box>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      <Link
                        to={article.link}
                        style={{ color: "black", textDecoration: "none" }}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="inherit"
                      >
                        {decodeHtml(article.title)}
                      </Link>
                    </Typography>

                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {article.description.length > 100
                        ? decodeHtml(article.description).substring(0, 100) + "..."
                        : decodeHtml(article.description)}
                    </Typography>

                    <Typography variant="body2" color="textSecondary">
                      {article.pubDate
                        ? new Date(article.pubDate).toLocaleDateString()
                        : t("esgAcademyFeed.noDate")}{" "}
                    </Typography>
                  </CardContent>
                  <CardActions
                    sx={{
                      justifyContent: "flex-end",
                      paddingX: 2,
                      paddingBottom: 2,
                    }}
                  >
                    <Link
                      target="_blank"
                      rel="noopener noreferrer"
                      to={article.link}
                      state={{ scrollTo: "recommendations" }}
                      style={{ textDecoration: "none" }}
                    >
                      <Button variant="contained" size="medium">
                        {t("esgAcademyFeed.explore")}
                      </Button>
                    </Link>
                  </CardActions>
                </Box>
              </Grid>
            ))}
          </Grid>
          {showAll && totalPages > 1 && (
            <Box mt={3} display="flex" justifyContent="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={(event, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Card>
  );
};

export default EsgAcademyFeed;
